/**
 * Pure function: given a click event's anchor rect and the current viewport,
 * return a popover layout that fits on-screen.
 *
 * Used by Reader and (via useWordPopover) VideoReader to position the word
 * popover. Returns null if the inputs are invalid.
 *
 * Rules:
 *  - On mobile (< 768px) we render a bottom-sheet that's always fully on-screen.
 *  - On desktop we try: right of the anchor → left of the anchor → center horizontally;
 *    and: below the anchor → above the anchor → largest remaining side.
 *  - All positions are clamped inside the viewport with safe margins.
 */
const DESKTOP_WIDTH = 380;
const DESKTOP_SIDEBAR_GUARD = 230; // reserve space for the Layout sidebar
const EDGE_MARGIN = 10;

export function computePopoverPosition(anchorRect, viewport = typeof window !== 'undefined' ? window : null) {
  if (!anchorRect || !viewport) return null;

  const vh = viewport.innerHeight;
  const vw = viewport.innerWidth;
  const isMobile = vw < 768;

  if (isMobile) {
    const maxHeight = Math.min(vh * 0.72, 560);
    return {
      top: Math.max(8, vh - maxHeight - 8),
      left: 8,
      width: vw - 16,
      maxHeight,
      isMobile: true,
    };
  }

  // Desktop
  const popoverWidth = DESKTOP_WIDTH;
  const popoverMaxHeight = Math.min(vh - 40, 550);

  const marginRight = 15;
  const marginLeft = DESKTOP_SIDEBAR_GUARD;
  const marginTop = EDGE_MARGIN;
  const marginBottom = EDGE_MARGIN;

  // ── Horizontal ──
  const spaceRight = vw - anchorRect.right - marginRight;
  const spaceLeft = anchorRect.left - marginLeft;

  const minLeft = marginLeft;
  const maxLeft = vw - popoverWidth - marginRight;

  let left;
  if (spaceRight >= popoverWidth + EDGE_MARGIN) {
    left = anchorRect.right + EDGE_MARGIN;
  } else if (spaceLeft >= popoverWidth + EDGE_MARGIN) {
    left = anchorRect.left - popoverWidth - EDGE_MARGIN;
  } else {
    // Neither side fits — center in available space, clamped
    left = Math.max(minLeft, (vw - popoverWidth) / 2);
  }
  left = Math.max(minLeft, Math.min(left, maxLeft));

  // ── Vertical ──
  // Try to align near the anchor vertically; clamp to viewport
  const spaceBelow = vh - anchorRect.bottom - marginBottom;
  const spaceAbove = anchorRect.top - marginTop;

  let top;
  if (spaceBelow >= popoverMaxHeight + EDGE_MARGIN) {
    top = anchorRect.bottom + 8;
  } else if (spaceAbove >= popoverMaxHeight + EDGE_MARGIN) {
    top = anchorRect.top - popoverMaxHeight - 8;
  } else {
    // Neither side has full room — use whichever side has more space, or center
    top = Math.max(marginTop, Math.round((vh - popoverMaxHeight) / 2));
  }

  const minTop = marginTop;
  const maxTop = vh - popoverMaxHeight - marginBottom;
  top = Math.max(minTop, Math.min(top, maxTop));

  return {
    top,
    left,
    width: popoverWidth,
    maxHeight: popoverMaxHeight,
    isMobile: false,
  };
}
